import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ContentType } from 'utils/getContentType';
import type { RootState } from './Store.ts';
import { assertPreloaded } from './assertPreloaded';
import type { PricingTotalType } from 'services/documents/entities';

export type Currency = {
  symbol: string;
  code: string;
  symbolPosition?: 'front' | 'back';
  decimalSeparator?: '.' | ',';
  thousandsSeparator?: '' | '.' | ',' | ' ';
  decimalPlaces?: number;
};

type DocumentSettingsState = {
  contentId: string | null;
  currencySettings: Currency | null;
  backgroundColor: string | null;
  isLocked: boolean;
  contentType: ContentType | null;
  totalType: PricingTotalType | null;
  displayDocumentTotal: boolean;
};

export const DEFAULT_CURRENCY_CODE = 'USD';

export const defaultCurrencySettings: Currency = {
  symbol: '$',
  code: DEFAULT_CURRENCY_CODE,
};

const initialState: DocumentSettingsState = {
  contentId: null,
  currencySettings: null,
  backgroundColor: null,
  isLocked: false,
  contentType: null,
  totalType: null,
  displayDocumentTotal: true,
};

const documentSettingsSlice = createSlice({
  name: 'documentSettings',
  initialState,
  reducers: {
    setCurrencySettings: (state, action: PayloadAction<Currency>) => {
      state.currencySettings = action.payload;
    },
    updateCurrency: (state, action: PayloadAction<Currency>) => {
      if (state.currencySettings) {
        state.currencySettings = action.payload;
      }
    },
    setInitialCurrencySettings: (state, action: PayloadAction<Currency>) => {
      state.currencySettings = { ...action.payload };
    },
    setDocumentBackgroundColor: (state, action: PayloadAction<string>) => {
      state.backgroundColor = action.payload;
    },
    setIsLocked: (state, action: PayloadAction<boolean>) => {
      state.isLocked = action.payload;
    },
    setContentType: (state, action: PayloadAction<ContentType>) => {
      state.contentType = action.payload;
    },
    setContentId: (state, action: PayloadAction<string>) => {
      state.contentId = action.payload;
    },
    setTotalType: (state, action: PayloadAction<PricingTotalType>) => {
      state.totalType = action.payload;
    },
    setDisplayDocumentTotal: (state, action: PayloadAction<boolean>) => {
      state.displayDocumentTotal = action.payload;
    },
  },
});

export const selectActiveCurrencySettings = (state: RootState): Currency | null => {
  return state.documentSettings.currencySettings;
};

export const selectActiveCurrencyCode = (state: RootState): string | null => {
  const currencySettings = selectActiveCurrencySettings(state);
  return currencySettings?.code ?? null;
};

export const selectActiveCurrencySymbol = (state: RootState): string | null => {
  const currencySettings = selectActiveCurrencySettings(state);
  return currencySettings?.symbol ?? null;
};

export const selectIsDocumentLocked = (state: RootState): boolean => state.documentSettings.isLocked;
export const selectContentType = (state: RootState): ContentType => assertPreloaded(state.documentSettings.contentType, 'contentType');
export const selectContentId = (state: RootState): string => assertPreloaded(state.documentSettings.contentId, 'contentId');
export const selectTotalType = (state: RootState): PricingTotalType => assertPreloaded(state.documentSettings.totalType, 'totalType');
export const selectDisplayDocumentTotal = (state: RootState): boolean => state.documentSettings.displayDocumentTotal;

export const {
  setCurrencySettings,
  updateCurrency,
  setInitialCurrencySettings,
  setDocumentBackgroundColor,
  setIsLocked,
  setContentId,
  setContentType,
  setTotalType,
  setDisplayDocumentTotal,
} = documentSettingsSlice.actions;

export const documentSettings = documentSettingsSlice.reducer;
